import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { NiiRunType, NiiRunStatus, ReportSummary } from './types/nii.types';
import ReleaseSelector from './ReleaseSelector';
import ReportCard from './ReportCard';
import GenerateReportDialog from './GenerateReportDialog';
import styles from './NiiLandingPage.module.css';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';

const NiiLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  // Release selection state (anchor-based, e.g. "25Q1")
  const [selectedRelease, setSelectedRelease] = useState<string>('');
  const [appliedRelease, setAppliedRelease] = useState<string | null>(null);

  // Reports state
  const [fetchedReports, setFetchedReports] = useState<ReportSummary[]>([]);
  const [reportsLoading, setReportsLoading] = useState<boolean>(false);

  // Generate dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const { addNotifications } = useSwobNotifications();

  // AbortController ref for cancelling in-progress report fetches
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch summary cards on mount using useQuery
  const {
    data: summaryData,
    isLoading: releasesLoading,
    error: releasesError
  } = useQuery({
    queryKey: ['nii-summary-cards'],
    queryFn: () => niiService.getReleaseSummaryCards(NiiRunType.FEATURE_IMPACT),
    retry: false,
    refetchOnWindowFocus: false
  });

  const availableAnchors = useMemo(() => {
    if (!summaryData) return [];
    // Only show releases that have at least one completed report
    const completedReleases = summaryData.cards
      .filter((card) => card.completedCount > 0)
      .map((card) => card.anchor);
    return completedReleases;
  }, [summaryData]);

  // Show error notification when summary cards fetch fails
  useEffect(() => {
    if (releasesError) {
      const message =
        releasesError instanceof AxiosError
          ? releasesError.response?.data?.message || releasesError.message
          : releasesError instanceof Error
            ? releasesError.message
            : 'Failed to load releases.';
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: message,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  }, [releasesError]);

  // Auto-load release from URL query param or sessionStorage
  useEffect(() => {
    if (availableAnchors.length === 0 || appliedRelease) return;
    const releaseParam = searchParams.get('release');
    const savedRelease = releaseParam || sessionStorage.getItem('nii-selected-release');
    if (savedRelease && availableAnchors.includes(savedRelease)) {
      setSelectedRelease(savedRelease);
      setAppliedRelease(savedRelease);
      fetchReports(savedRelease);
    }
  }, [availableAnchors, searchParams]);

  const handleApplyRelease = () => {
    if (!selectedRelease.trim()) return;
    setAppliedRelease(selectedRelease);
    setSearchParams({ release: selectedRelease }, { replace: true });
    sessionStorage.setItem('nii-selected-release', selectedRelease);
    fetchReports(selectedRelease);
  };

  const handleReleaseSelect = (anchor: string) => {
    setSelectedRelease(anchor);
  };

  const fetchReports = async (releaseName: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setReportsLoading(true);
    try {
      const response = await niiService.getReports({
        releaseName,
        status: NiiRunStatus.COMPLETE
      });
      if (controller.signal.aborted) return;
      setFetchedReports(response.data);
    } catch (error) {
      if (controller.signal.aborted) return;
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred fetching reports',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      if (!controller.signal.aborted) {
        setReportsLoading(false);
      }
    }
  };

  const handleViewReport = (report: ReportSummary) => {
    if (report.type === NiiRunType.FEATURE_IMPACT) {
      navigate(`/nii/impact-reports/feature/${report.reportId}`);
    } else if (report.type === NiiRunType.TR_IMPACT) {
      navigate(`/nii/impact-reports/tr/${report.reportId}`);
    }
  };

  const handleRegenerate = (report: ReportSummary) => {
    const releaseName = report.releaseName || appliedRelease || '';
    const typeLabel = report.type === NiiRunType.FEATURE_IMPACT ? 'Feature Impact' : 'TR Impact';

    const dataSource =
      report.dataSourceKind === 'upload' && report.uploadId
        ? { kind: 'upload' as const, uploadId: report.uploadId }
        : { kind: 'enm_live' as const, enmEndpointIds: report.enmEndpointId ? [report.enmEndpointId] : [] };

    niiService
      .createRuns({
        runs: [
          {
            type: report.type,
            name: `${releaseName} ${typeLabel}`,
            release: { name: releaseName },
            dataSource
          }
        ]
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['nii-runs'] });
        navigate('/nii/executions');
      })
      .catch((error) => {
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description:
              error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'Failed to trigger regeneration.',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      });
  };

  const handleGenerateSubmit = () => {
    setShowGenerateDialog(false);
    queryClient.invalidateQueries({ queryKey: ['nii-runs'] });
    navigate('/nii/executions');
  };

  const applyDisabled = !selectedRelease.trim() || reportsLoading;

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          <div className={styles.pageHeader}>
            <div>
              <div className={styles.settingsTitle}>Impact Reports</div>
              <p className={styles.subtitle}>
                AI-based solution that generate tailored, customer-specific impact intelligence for RAN software releases.
                <br></br>
                Enhance days of new feature deep dives analysis to minutes of automated, customizable impact intelligence.
              </p>
            </div>
            <ReactEDSWrapper component={Button} primary onClick={() => setShowGenerateDialog(true)}>
              <i className="icon icon-plus" style={{ marginRight: '4px' }} />
              Create New Report
            </ReactEDSWrapper>
          </div>

          <div className={styles.releaseSelectorRow}>
            <ReleaseSelector
              anchors={availableAnchors}
              selectedAnchor={selectedRelease || null}
              onSelect={handleReleaseSelect}
              loading={releasesLoading}
            />
            <ReactEDSWrapper component={Button} primary onClick={handleApplyRelease} disabled={applyDisabled}>
              Load
            </ReactEDSWrapper>
          </div>

          {reportsLoading && (
            <div className={styles.loading}>
              <div className="loader small" />
              <p>Loading reports…</p>
            </div>
          )}

          {!reportsLoading && !appliedRelease && (
            <div className={styles.noDataContainer}>
              <i className={`icon icon-info ${styles.noDataIcon}`} />
              <div className={styles.noDataTitle}>No release loaded</div>
              <p className={styles.subtitle}>Choose a release above, then click &quot;Load&quot; to view impact data.</p>
            </div>
          )}

          {!reportsLoading && appliedRelease && fetchedReports.length === 0 && (
            <div className={styles.noDataContainer}>
              <i className={`icon icon-info ${styles.noDataIcon}`} />
              <div className={styles.noDataTitle}>No reports available</div>
              <p className={styles.subtitle}>No completed reports available for the selected release.</p>
            </div>
          )}

          {!reportsLoading &&
            appliedRelease &&
            fetchedReports.length > 0 &&
            (() => {
              const trReports = fetchedReports.filter((r) => r.type === NiiRunType.TR_IMPACT);
              const featureReports = fetchedReports.filter((r) => r.type === NiiRunType.FEATURE_IMPACT);

              const renderCard = (report: ReportSummary) => (
                <ReportCard
                  key={report.reportId}
                  title={report.type === NiiRunType.FEATURE_IMPACT ? 'Feature Impact (Playbook)' : 'Trouble Report Impact'}
                  status={NiiRunStatus.COMPLETE}
                  onView={() => handleViewReport(report)}
                  onRegenerate={() => handleRegenerate(report)}
                  createdAt={report.createdAt}
                  createdBy={report.createdBy}
                  dataSourceKind={report.dataSourceKind}
                  enmEndpointName={report.enmEndpointName}
                  uploadFilename={report.uploadFilename}
                />
              );

              return (
                <div className={styles.reportColumns}>
                  <div className={styles.reportColumn}>{trReports.map(renderCard)}</div>
                  <div className={styles.reportColumn}>{featureReports.map(renderCard)}</div>
                </div>
              );
            })()}
        </div>
      </div>

      <GenerateReportDialog open={showGenerateDialog} onClose={() => setShowGenerateDialog(false)} onSubmit={handleGenerateSubmit} />
    </div>
  );
};

export default NiiLandingPage;
